import { useSearch } from "@tanstack/react-router";
import { type PropsWithChildren, useEffect } from "react";

import {
  AnalyticIdentify,
  PageViewTrackingProvider,
} from "@/features/my/shell";
import { useAnalytics } from "@/libs/analytics";

/**
 * FIRST touch only, and module-scoped rather than a ref: a later step's URL no longer
 * carries the params (`keepCampaignSearch` drops them by design), so a remount must not be
 * able to overwrite the campaign that brought the buyer in.
 */
let utmCaptured = false;

/**
 * The funnel-wide tracking the presale steps share, mounted once by the `/presale` layout
 * route — the same arrangement `features/join`'s layout uses, and for the same reason: an
 * effect that must run once per FUNNEL cannot live on a per-step component.
 *
 * Three concerns, all reused rather than reimplemented:
 *
 * - First-touch UTM into analytics super props, so every `Presale.*` event carries the
 *   campaign attribution (mirrors `features/join/index.tsx`).
 * - `AnalyticIdentify` — the alias + identify pair. This is the ONLY thing that links the
 *   anonymous id which fired `Presale.AddToCart` to the real user, and presale needs it
 *   mounted here because it never enters the `(protected)` tree that mounts it for `/my`.
 * - `PageViewTrackingProvider` — one `Activity.PageViewed` per step navigation.
 */
export function PresaleFunnelTracking({ children }: PropsWithChildren) {
  const { setSuperProps } = useAnalytics();
  const search = useSearch({ strict: false }) as Record<string, unknown>;

  useEffect(() => {
    if (utmCaptured) return;
    const present = Object.fromEntries(
      Object.entries({
        utm_source: search.utm_source,
        utm_medium: search.utm_medium,
        utm_campaign: search.utm_campaign,
      }).filter(([, value]) => value != null && value !== ""),
    );
    // Nothing to register is NOT "clear what we had" — bail rather than register({}).
    if (Object.keys(present).length === 0) return;
    utmCaptured = true;
    setSuperProps?.(present);
  }, []);

  return (
    <AnalyticIdentify>
      <PageViewTrackingProvider>{children}</PageViewTrackingProvider>
    </AnalyticIdentify>
  );
}
